// src/components/templates/diary/LearningMarketplace.jsx
// Course Marketplace - Browse, Create, and Enroll in Courses
import React, { useState, useMemo } from 'react';
import {
  Store, Search, Grid, List, Filter, Plus, BookOpen, Clock, Users,
  Star, DollarSign, CheckCircle, Play, X, ChevronRight, Sparkles,
  TrendingUp, Award, Target, Zap
} from 'lucide-react';
import CourseCreationModal from './CourseCreationModal';
import { CourseCategory, CourseDifficulty } from '../../constants/learningConstants';

const LearningMarketplace = ({
  bookId,
  user,
  isOwner,
  enrollments = [],
  courses = [],
  customStyling = {},
  onEnroll,
  onAddCourse,
  onUpdateCourse,
  onDeleteCourse,
  onSaveLesson,
  onBack,
  diaryService
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const [showFeatured, setShowFeatured] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  // Get courses visible to current user
  const visibleCourses = useMemo(() => {
    return courses.filter(c => {
      // Show published courses to everyone
      if (c.isPublished) return true;
      
      // Show creator's own courses (including drafts)
      if (user && c.createdBy === user.uid) return true;
      
      // Show all to book owner
      if (isOwner) return true;
      
      return false;
    });
  }, [courses, user, isOwner]);

  // Filter courses
  const filteredCourses = useMemo(() => {
    let filtered = visibleCourses;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.title?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.instructor?.toLowerCase().includes(q)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }

    if (selectedDifficulty) {
      filtered = filtered.filter(c => c.difficulty === selectedDifficulty);
    }

    if (showFreeOnly) {
      filtered = filtered.filter(c => c.isFree);
    }

    if (showFeatured) {
      filtered = filtered.filter(c => c.featured);
    }

    return filtered;
  }, [visibleCourses, searchQuery, selectedCategory, selectedDifficulty, showFreeOnly, showFeatured]);

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.courseId === courseId);
  };

  const isCreator = (course) => {
    return user && (course.createdBy === user.uid || isOwner);
  };

  const handleEnroll = async (course) => {
    if (!user) {
      alert('Please sign in to enroll in courses');
      return;
    }

    if (isEnrolled(course.id)) {
      alert('You are already enrolled in this course');
      return;
    }

    if (!course.isFree) {
      // Handle payment integration here
      alert('Payment integration coming soon!');
      return;
    }

    if (onEnroll) {
      await onEnroll(course.id);
    }
  };

  // Course Detail Modal
  if (selectedCourse) {
    const enrolled = isEnrolled(selectedCourse.id);
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Course Details</h2>
            <button
              onClick={() => setSelectedCourse(null)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Thumbnail */}
            <div className="aspect-video bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl overflow-hidden">
              {selectedCourse.thumbnail.url ? (
                <img src={selectedCourse.thumbnail.url} alt={selectedCourse.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-white opacity-50" />
                </div>
              )}
            </div>

            {/* Title & Instructor */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedCourse.title}</h3>
              <p className="text-lg text-gray-600">by {selectedCourse.instructor}</p>
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg">
                <Target className="w-4 h-4" />
                {selectedCourse.difficulty}
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg">
                <Sparkles className="w-4 h-4" />
                {selectedCourse.category}
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg">
                <BookOpen className="w-4 h-4" />
                {selectedCourse.lessonCount || 0} lessons
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg">
                <Clock className="w-4 h-4" />
                {Math.round((selectedCourse.estimatedDuration || 0) / 60)}h
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg">
                <Users className="w-4 h-4" />
                {selectedCourse.enrollmentCount || 0} enrolled
              </div>
              {selectedCourse.rating > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg">
                  <Star className="w-4 h-4 fill-yellow-400" />
                  {selectedCourse.rating.toFixed(1)}
                </div>
              )}
            </div>

            {/* Price */}
            <div>
              {selectedCourse.isFree ? (
                <span className="inline-block px-4 py-2 bg-green-100 text-green-700 font-semibold rounded-lg">
                  FREE
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-gray-600" />
                  <span className="text-2xl font-bold text-gray-900">
                    {selectedCourse.price}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">About This Course</h4>
              <p className="text-gray-600 whitespace-pre-wrap">{selectedCourse.description}</p>
            </div>

            {/* Learning Objectives */}
            {selectedCourse.learningObjectives?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What You'll Learn</h4>
                <ul className="space-y-2">
                  {selectedCourse.learningObjectives.map((objective, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span>{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Enroll Button */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 -mx-6 -mb-6 px-6 py-4">
              {enrolled ? (
                <button
                  disabled
                  className="w-full py-3 bg-gray-100 text-gray-500 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" /> Already Enrolled
                </button>
              ) : (
                <button
                  onClick={() => handleEnroll(selectedCourse)}
                  className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-semibold flex items-center justify-center gap-2"
                  style={{ backgroundColor: customStyling.primaryColor }}
                >
                  <Play className="w-5 h-5" /> Enroll Now {!selectedCourse.isFree && `- $${selectedCourse.price}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Course Creation Modal
  // Course Creation/Edit Modal
  if (showCreateModal || editingCourse) {
    return (
      <CourseCreationModal
        bookId={bookId}
        user={user}
        editCourse={editingCourse}
        onClose={() => {
          setShowCreateModal(false);
          setEditingCourse(null);
        }}
        onSave={async (courseData) => {
          if (onAddCourse) {
            const courseId = await onAddCourse(courseData);
            return courseId;
          }
        }}
        onUpdateCourse={onUpdateCourse}
        onSaveLesson={onSaveLesson}
        diaryService={diaryService}
      />
    );
  }

  // Main Marketplace View
  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-6 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Store className="w-8 h-8 text-indigo-600" />
                Course Marketplace
              </h1>
              <p className="text-gray-600 mt-1">{filteredCourses.length} courses available</p>
            </div>
          </div>
          
          {/* Create Course Button (for creators) */}
          {isOwner && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create Course
            </button>
          )}
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search courses..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {/* Category Filter */}
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All Categories</option>
              {Object.values(CourseCategory).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty || ''}
              onChange={(e) => setSelectedDifficulty(e.target.value || null)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All Levels</option>
              {Object.values(CourseDifficulty).map(diff => (
                <option key={diff} value={diff}>{diff}</option>
              ))}
            </select>

            {/* Free Only Toggle */}
            <button
              onClick={() => setShowFreeOnly(!showFreeOnly)}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                showFreeOnly
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Free Only
            </button>

            {/* Featured Toggle */}
            <button
              onClick={() => setShowFeatured(!showFeatured)}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                showFeatured
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Star className="w-4 h-4 inline mr-1" />
              Featured
            </button>

            {/* View Mode Toggle */}
            <div className="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Courses Grid/List */}
        {filteredCourses.length > 0 ? (
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }>
            {filteredCourses.map(course => (
              <CourseCard
                key={course.id}
                course={course}
                enrolled={isEnrolled(course.id)}
                isCreator={isCreator(course)}
                viewMode={viewMode}
                onClick={() => setSelectedCourse(course)}
                onEdit={(course) => {
                  setEditingCourse(course);
                }}
                onDelete={async (courseId) => {
                  if (onDeleteCourse) {
                    await onDeleteCourse(courseId);
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-gray-100 p-6 rounded-full inline-block mb-4">
              <Search className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Courses Found</h3>
            <p className="text-gray-600">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== COURSE CARD ====================

const CourseCard = ({ course, enrolled, isCreator, viewMode, onClick, onEdit, onDelete }) => {
  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow flex gap-4">
        <div 
          onClick={onClick}
          className="flex-1 flex gap-4 cursor-pointer"
        >
          <div className="w-48 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shrink-0 overflow-hidden">
            {course.thumbnail.url ? (
              <img src={course.thumbnail.url} alt={course.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white opacity-50" />
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{course.title}</h3>
                <p className="text-sm text-gray-600">{course.instructor}</p>
              </div>
              <div className="flex items-center gap-2">
                {!course.isPublished && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    DRAFT
                  </span>
                )}
                {course.isFree ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    FREE
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                    ${course.price}
                  </span>
                )}
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>
            
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                {course.lessonCount || 0} lessons
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {Math.round((course.estimatedDuration || 0) / 60)}h
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {course.enrollmentCount || 0}
              </span>
              {course.rating > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  {course.rating.toFixed(1)}
                </span>
              )}
            </div>
          </div>

          {enrolled && !isCreator && (
            <div className="flex items-center">
              <span className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Enrolled
              </span>
            </div>
          )}
        </div>
        
        {/* Creator Controls */}
        {isCreator && (
          <div className="flex flex-col gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(course);
              }}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Delete this course?')) onDelete(course.id);
              }}
              className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group relative">
      <div onClick={onClick} className="cursor-pointer">
        <div className="aspect-video bg-gradient-to-br from-indigo-500 to-purple-600 relative">
          {course.thumbnail?.url ? (
            <img src={course.thumbnail.url} alt={course.title} className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <BookOpen className="w-12 h-12 text-white opacity-50" />
            </div>
          )}
          {!course.isPublished && (
            <div className="absolute top-3 left-3 bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              DRAFT
            </div>
          )}
          {course.featured && (
            <div className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Star className="w-3 h-3 fill-current" /> Featured
            </div>
          )}
          {enrolled && !isCreator && (
            <div className="absolute top-3 right-3 bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Enrolled
            </div>
          )}
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-indigo-600">
                {course.title}
              </h3>
              <p className="text-sm text-gray-600">{course.instructor}</p>
            </div>
            {course.isFree ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded ml-2">
                FREE
              </span>
            ) : (
              <span className="text-indigo-600 font-semibold ml-2">${course.price}</span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>

          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              {course.lessonCount || 0}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {Math.round((course.estimatedDuration || 0) / 60)}h
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {course.enrollmentCount || 0}
            </span>
            {course.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                {course.rating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Creator Controls */}
      {isCreator && (
        <div className="px-4 pb-4 flex gap-2 border-t pt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(course);
            }}
            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
          >
            Edit Course
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Delete this course? This cannot be undone.')) onDelete(course.id);
            }}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default LearningMarketplace;